import ChangePwdDialog from "@/app/account/components/ChangePwdDialog"
import ProfileUploader from "@/components/profileUploader/ProfileUploader"
import { createAccountStore } from "@/stores/account"
import { getImagePath } from "@/utils/image"
import { useEffect, useMemo, useState } from "react"
import moment from "moment"
import { isEqual } from "lodash"

import { useTranslation } from "react-i18next"

import "./page.scss"

import TextField from "@/components/form/textField/TextField"
import DatePicker from "@/components/form/datePicker/DatePicker"
import Selector from "@/components/form/selector/Selector"

import { createGlobalStore } from "@/stores/global"
import { MenuItem } from "@mui/material"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import { Country } from "@/types/account"
import BasicButton from "@/components/button/BasicButton"
import {
  useCreateProfileImageMutation,
  useDeleteProfileImageMutation,
  useUpsertUserInfoMutation,
} from "@/hooks/api/useAccountQuery"

export default function AccountPage() {
  const [open, setOpen] = useState(false)
  const [localImgUrl, setLocalImgUrl] = useState<string>("")

  const [userName, setUserName] = useState<string>("")
  const [birth, setBirth] = useState<Date | null>(null)
  const [country, setCountry] = useState<string>("")
  const [gender, setGender] = useState<string>("")
  const [email, setEmail] = useState<string>("")

  const [userNameErrorMsg, setUserNameErrorMsg] = useState<string | undefined>()

  const { account, setInfo } = createAccountStore((state) => ({
    account: state.info,
    setInfo: state.setInfo,
  }))
  const { countryList, setSnackBar } = createGlobalStore((state) => ({
    countryList: state.countryList,
    setSnackBar: state.setSnackBar,
  }))

  const { t } = useTranslation("")
  const { mutate: upsertInfo, isSuccess } = useUpsertUserInfoMutation()
  const { mutateAsync: upsertImage } = useCreateProfileImageMutation()
  const { mutateAsync: deleteImage } = useDeleteProfileImageMutation()

  const genderList = [
    { label: t("male"), value: "M" },
    { label: t("female"), value: "F" },
  ]

  const convertCountry = (country: string) => {
    return countryList.find((item) => item.code === country)
  }

  const updateImage = (file?: Blob) => {
    if (file) {
      const form = new FormData()
      form.append("idfAccount", account?.idfAccount?.toString() || "")
      form.append("fileImage", file)

      upsertImage(form).then((resp) => {
        const { response } = resp

        if (response && account) {
          const { image } = response
          setLocalImgUrl(getImagePath(image))
          setInfo({ ...account, image }) // AccountStore 업데이트
          setSnackBar({ message: t("messages.successUpdate"), severity: "success", open: true })
        }
      })
    }
  }

  const removeImage = () => {
    deleteImage(account?.idfAccount?.toString() as string).then(() => {
      if (account) {
        setLocalImgUrl("")
        setInfo({ ...account, image: "" }) // AccountStore 업데이트
        setSnackBar({ message: t("messages.successDelete"), severity: "success", open: true })
      }
    })
  }

  const updateInfo = () => {
    if (!account) {
      return
    }

    upsertInfo({
      idfAccount: account?.idfAccount,
      payload: {
        ...account,
        name: userName,
        birthday: moment(birth as Date).format("YYYY-MM-DD"),
        country,
        gender,
      },
    })
  }

  useEffect(() => {
    setLocalImgUrl(getImagePath(account?.image))
  }, [account?.image])

  useEffect(() => {
    setUserName(account?.name || "")
    setBirth(account?.birthday ? new Date(account.birthday) : null)
    setCountry(account?.country || "")
    setGender(account?.gender || "")
    setEmail(account?.email || "")
  }, [account?.idfAccount])

  useEffect(() => {
    if (userName.length > 35) {
      setUserNameErrorMsg(t("rules.max_len", { name: "name", max_len: 35 }))

      return
    }

    if (userName === "") {
      setUserNameErrorMsg(t("rules.required"))

      return
    }

    setUserNameErrorMsg(undefined)
  }, [userName, t])

  useEffect(() => {
    if (isSuccess) {
      setSnackBar({ open: true, message: t("messages.successUpdate"), severity: "success" })
    }
  }, [isSuccess])

  const isDisabledSaveBtn = useMemo(() => {
    if (!account || !!userNameErrorMsg) {
      return true
    }

    // 현재 입력값과 서버 상태 비교
    const currentData = {
      name: userName,
      birthday: birth ? moment(birth).format("YYYY-MM-DD") : "",
      country,
      gender,
    }

    const originalData = {
      name: account.name || "",
      birthday: account.birthday || "",
      country: account.country || "",
      gender: account.gender || "",
    }

    // 변경사항이 없으면 비활성화
    return isEqual(currentData, originalData)
  }, [account, userName, birth, country, gender, userNameErrorMsg])

  return (
    <div className="AccountPageCont">
      <div className="account-page-card">
        <ProfileUploader
          imgUrl={localImgUrl}
          onSave={updateImage}
          onRemove={removeImage}
          width={100}
          height={100}
        />
        <div className="account-page-layout">
          <div className="account-page-layout__item">
            <label className="account-page-layout__item__label">{t("name")}</label>
            <TextField
              fullWidth
              value={userName}
              maxLength={35}
              onChange={(e) => setUserName((e.target as HTMLInputElement).value)}
              error={!!userNameErrorMsg}
              helperText={userNameErrorMsg}
            />
          </div>
          <div className="account-page-layout__item">
            <label className="account-page-layout__item__label">{t("password")}</label>
            <BasicButton onClick={() => setOpen(true)} size="md" type="default-outlined">
              {t("button.change")}
            </BasicButton>
          </div>
          <div className="account-page-layout__item">
            <label className="account-page-layout__item__label">{t("birth")}</label>
            <DatePicker value={birth} onChange={(date) => setBirth(date)} sx={{ width: "100%" }} />
          </div>
          <div className="account-page-layout__item">
            <label className="account-page-layout__item__label">{t("nationality")}</label>
            <Selector
              value={convertCountry(country)?.name || ""}
              labelKey="name"
              onChange={(_event: React.SyntheticEvent, value: Country) => {
                setCountry(value.code)
              }}
              options={country ? countryList : []}
              sx={{ width: "100%" }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props

                return (
                  <MenuItem key={key} value={option.code} {...optionProps}>
                    {option.name}
                  </MenuItem>
                )
              }}></Selector>
          </div>
          <div className="account-page-layout__item">
            <label className="account-page-layout__item__label">{t("gender")}</label>
            <RadioGroup
              valueKey="value"
              labelKey="label"
              items={genderList}
              direction="row"
              onChange={(e) => setGender(e.target.value)}
              value={gender}
            />
          </div>
          <div className="account-page-layout__item">
            <label className="account-page-layout__item__label">{t("email")}</label>
            <TextField
              fullWidth
              value={email}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
            />
          </div>
        </div>
      </div>
      <ChangePwdDialog open={open} onClose={() => setOpen(false)} />
      <BasicButton
        disabled={isDisabledSaveBtn}
        onClick={updateInfo}
        size="lg"
        type="primary-contained">
        {t("button.save")}
      </BasicButton>
    </div>
  )
}
